import { Grid, TableHead } from '@mui/material';
import { t } from 'i18next';
import React, { useContext, useEffect, useState } from 'react'
import PrimaryButtonGroup from '../buttons/PrimaryButtonGroup';
import BigLoader from '../common/BigLoader';
import { getTranslation } from '../models/Translation';
import AlternatingTableRow from '../table/AlternatingTableRow';
import ScrollableTable from '../table/ScrollableTable';
import StandardTableCell from '../table/StandardTableCell';
import TableHeadCell, { QueryOrder } from '../table/TableHeadCell';
import SnackbarContext from '../contexts/SnackbarContext';
import UserContext from '../contexts/UserContext';
import { getMenuItemStatistics } from '../config/apiRoutes';
import { MenuItemClick, MenuItemClicks } from '../models/Statistics';
import useFetch from '../hooks/useFetch';
import { RoleType } from '../models/Role';
import { hasRole } from '../models/User';

type Props = {}

const ClickCountTable = (props: Props) => {
    const [menuItemPeriod, setMenuItemPeriod] = useState<'last7days' | 'last30days'>('last30days');
    const snackbar = useContext(SnackbarContext);
    const { selectedRestaurant, user, hasFeature } = useContext(UserContext);
    const [menuItemStatistics, setMenuItemStatistics] = useState<MenuItemClicks | null>(null);
    const hasAccess = hasRole(user, RoleType.OWNER) && hasFeature("ANALYTICS_INSIGHTS");
    const [orderBy, setOrderBy] = useState<QueryOrder | null>({ orderBy: "count", orderDir: "desc" });
    const menuItemCaller = useFetch();

    const displayedStatistics: MenuItemClick[] = React.useMemo(() => {
        if (menuItemStatistics == null) return [];
        const usedItems = [...menuItemStatistics?.[menuItemPeriod] || []];
        if (orderBy?.orderBy === "name") {
            return usedItems.sort(compareItemsByName);
        }
        return usedItems.sort(compareItemsByClick);
    }, [menuItemStatistics, orderBy, menuItemPeriod]);

    function compareItemsByName(a: MenuItemClick, b: MenuItemClick) {
        const titleA = getTranslation(a.item.translations).title.toLowerCase();
        const titleB = getTranslation(b.item.translations).title.toLowerCase();
        if (orderBy?.orderDir === 'asc') {
            return titleA.localeCompare(titleB);
        }
        return titleB.localeCompare(titleA);
    }

    function compareItemsByClick(a: MenuItemClick, b: MenuItemClick) {
        if (orderBy?.orderDir === 'asc') {
            return parseInt(a.count) - parseInt(b.count);
        }
        return parseInt(b.count) - parseInt(a.count);
    }

    useEffect(() => {
        if (selectedRestaurant && hasAccess) {
            fetchMenuItemStatistics();
        }
    }, [selectedRestaurant, hasAccess]);

    function handleMenuItemPeriodChange(period: 'last7days' | 'last30days') {
        if (period === menuItemPeriod) return;
        setMenuItemPeriod(period);
    }

    async function fetchMenuItemStatistics() {
        const url = getMenuItemStatistics.replace(":restaurantShortName", selectedRestaurant?.restaurantShortName || "");
        const response = await menuItemCaller.call<undefined, MenuItemClicks>("GET", url);
        if (response.ok && response.data) {
            setMenuItemStatistics(response.data);
        } else {
            snackbar.showMessage(t("statistics.error_fetching_menu_item_statistics"), "error");
        }
    }

    return (
        <>
            <Grid container>
                <Grid item xs>
                    <div className="pb-4 pt-1 text-xl font-bold text-primary-dark">
                        {t("statistics.menu_item_clicks")}
                    </div>
                </Grid>
            </Grid>
            <div className="mb-2">
                <PrimaryButtonGroup
                    variant="outlined"
                    fullWidthOnMobile={true}
                    buttons={[
                        { label: t("statistics.last_7_days"), onClick: () => handleMenuItemPeriodChange('last7days'), isSelected: menuItemPeriod === 'last7days' },
                        { label: t("statistics.last_30_days"), onClick: () => handleMenuItemPeriodChange('last30days'), isSelected: menuItemPeriod === 'last30days' },
                    ]}
                />
            </div>
            <ScrollableTable>
                <TableHead>
                    <tr className="h-[56px] bg-neutral-deep">
                        <TableHeadCell className="w-3/4" orderKey="name" order={orderBy} setOrder={setOrderBy} showOrderIcon={true}>
                            {t("statistics.menu_item")}
                        </TableHeadCell>
                        <TableHeadCell align="right" orderKey="count" order={orderBy} setOrder={setOrderBy} showOrderIcon={true}>
                            {t("statistics.click_count")}
                        </TableHeadCell>
                    </tr>
                </TableHead>
                <tbody>
                    {menuItemCaller.loading ? (
                        <tr>
                            <td colSpan={2}>
                                <BigLoader />
                            </td>
                        </tr>
                    ) : (displayedStatistics
                        .map((row, index) => {
                            const translation = getTranslation(row.item.translations);
                            return (
                                <AlternatingTableRow key={`menu-item-${row.item_id}`} index={index}>
                                    <StandardTableCell>{translation.title}</StandardTableCell>
                                    <StandardTableCell align="right">{row.count}</StandardTableCell>
                                </AlternatingTableRow>
                            );
                        })
                    )}
                </tbody>
            </ScrollableTable>

        </>
    )
}

export default ClickCountTable